/**
 * FootstepCadence — pure distance accumulator for footstep audio events.
 *
 * Tracks grounded horizontal travel and fires a step trigger whenever
 * accumulated distance passes a stride threshold. No Babylon.js deps;
 * fully testable in a node/vitest environment.
 *
 * Usage in PlayerEntity.applyInput (after grounded detection):
 *   if (this.cadence.update(Math.abs(vx), deltaTime, this.isGrounded)) {
 *     // emit PLAYER_FOOTSTEP
 *   }
 */
export class FootstepCadence {
  private readonly strideDistance: number;
  private accumulator: number = 0;

  /**
   * @param strideDistance - Distance (in world units) per footstep trigger.
   *   Minna uses 0.6; Basil uses 0.9 (heavier, longer stride).
   */
  constructor(strideDistance: number) {
    this.strideDistance = strideDistance;
  }

  /**
   * Call once per frame. Returns true when a step should be triggered.
   *
   * Accumulates `speed * dt` while grounded and at or above minSpeed.
   * When the accumulator reaches strideDistance, it subtracts one stride
   * and returns true. Returns false otherwise.
   * Resets the accumulator when the character is airborne or below threshold.
   *
   * @param speed     - Horizontal speed magnitude (u/s). Pass Math.abs(vx).
   * @param dt        - Frame delta time in seconds.
   * @param grounded  - Whether the character is on the ground.
   * @param minSpeed  - Minimum speed required to accumulate (default 0.5 u/s).
   */
  update(speed: number, dt: number, grounded: boolean, minSpeed = 0.5): boolean {
    if (!grounded || speed < minSpeed) {
      this.accumulator = 0;
      return false;
    }

    this.accumulator += speed * dt;

    if (this.accumulator >= this.strideDistance) {
      this.accumulator -= this.strideDistance;
      return true;
    }

    return false;
  }
}
